import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { LogOut, Menu, Moon, Sun, X } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";

const LINKS = [
    { to: "/", label: "Home" },
    { to: "/exams", label: "Exams" },
    { to: "/panel", label: "Panel" },
    { to: "/report", label: "Sample Report" },
    { to: "/about", label: "About" },
];

const navLinkClass = ({ isActive }) =>
    `u-link text-sm tracking-wide transition-colors duration-300 ${
        isActive ? "active text-brass" : "text-ink hover:text-brass"
    }`;

export const Navbar = ({ theme, onToggleTheme }) => {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const { user, userLoading, logout } = useAuth();

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    const authSlot = (mobile = false) => {
        if (userLoading) return null;
        if (user) {
            return (
                <span
                    data-testid="navbar-user-chip"
                    className={
                        mobile
                            ? "border-b border-line py-4 text-sm text-brass"
                            : "text-sm text-brass"
                    }
                >
                    Hi, {(user.name || user.email).split(" ")[0]}
                </span>
            );
        }
        if (mobile) {
            return (
                <NavLink
                    to="/login"
                    data-testid="mobile-nav-link-sign-in"
                    className={({ isActive }) =>
                        `border-b border-line py-4 text-base ${
                            isActive ? "text-brass" : "text-ink"
                        }`
                    }
                >
                    Sign in
                </NavLink>
            );
        }
        return (
            <NavLink
                to="/login"
                data-testid="nav-link-sign-in"
                className={navLinkClass}
            >
                Sign in
            </NavLink>
        );
    };

    const studioSlot = (mobile = false) => {
        if (userLoading || !user || user.role !== "admin") return null;
        const item = (to, label, testid) => (
            <NavLink
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                    mobile
                        ? `border-b border-line py-4 text-base ${isActive ? "text-brass" : "text-ink"}`
                        : `u-link text-sm tracking-wide transition-colors duration-300 ${isActive ? "active text-brass" : "text-ink hover:text-brass"}`
                }
            >
                {label}
            </NavLink>
        );
        return (
            <>
                {item("/studio", "AI Studio", mobile ? "mobile-nav-link-studio" : "nav-link-studio")}
                {item("/admin/leads", "Leads", mobile ? "mobile-nav-link-leads" : "nav-link-leads")}
            </>
        );
    };

    return (
        <header className="sticky top-0 z-50 border-b border-line bg-paper">
            <div className="wrap flex h-16 items-center justify-between md:h-20">
                <Link
                    to="/"
                    className="flex items-center gap-3"
                    data-testid="nav-brand"
                >
                    <Logo className="h-8 w-8 md:h-9 md:w-9" />
                    <span className="font-display text-xl tracking-tight md:text-2xl">
                        The Panel Room
                    </span>
                </Link>

                <nav className="hidden items-center gap-8 lg:flex">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            className={navLinkClass}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    {studioSlot(false)}
                    {authSlot(false)}
                </nav>

                <div className="flex items-center gap-2 sm:gap-4">
                    {user && !userLoading && (
                        <button
                            type="button"
                            onClick={logout}
                            aria-label="Sign out"
                            data-testid="navbar-logout-btn"
                            title="Sign out"
                            className="flex h-10 w-10 items-center justify-center border border-line text-ink-muted transition-colors duration-300 hover:border-brass hover:text-brass"
                        >
                            <LogOut size={16} strokeWidth={1.25} />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onToggleTheme}
                        aria-label="Toggle dark mode"
                        data-testid="dark-mode-toggle"
                        className="flex h-10 w-10 items-center justify-center border border-line text-ink-muted transition-colors duration-300 hover:border-brass hover:text-brass"
                    >
                        {theme === "dark" ? (
                            <Sun size={17} strokeWidth={1.25} />
                        ) : (
                            <Moon size={17} strokeWidth={1.25} />
                        )}
                    </button>

                    <Link
                        to="/book"
                        data-testid="nav-cta"
                        className="hidden bg-pine px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.16em] text-paper transition-colors duration-300 hover:bg-brass md:inline-flex"
                    >
                        Book a mock board
                    </Link>

                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                        aria-expanded={open}
                        data-testid="mobile-menu-button"
                        className="flex h-10 w-10 items-center justify-center border border-line text-ink transition-colors duration-300 hover:border-brass hover:text-brass lg:hidden"
                    >
                        {open ? (
                            <X size={18} strokeWidth={1.25} />
                        ) : (
                            <Menu size={18} strokeWidth={1.25} />
                        )}
                    </button>
                </div>
            </div>

            {open && (
                <nav
                    className="border-t border-line bg-paper lg:hidden"
                    data-testid="mobile-menu"
                >
                    <div className="wrap flex flex-col py-4">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                className={({ isActive }) =>
                                    `border-b border-line py-4 text-base ${
                                        isActive ? "text-brass" : "text-ink"
                                    }`
                                }
                                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        {studioSlot(true)}
                        {authSlot(true)}
                        <Link
                            to="/book"
                            data-testid="mobile-nav-cta"
                            className="btn-primary mt-4 mb-2"
                        >
                            Book a mock board
                        </Link>
                    </div>
                </nav>
            )}
        </header>
    );
};
